import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { CampaignSummary, DonationDetail as Detail } from '@foundation/shared';
import {
  DONATION_CHANNEL_LABEL_TH,
  DONATION_STATUS_LABEL_TH,
} from '@foundation/shared';
import { api, openAuthedDocument, tokenStore } from '../lib/api';
import { formatDateTime, formatSatang } from '../lib/format';
import { ErrorAlert, Loading, Modal } from '../components/ui';
import { useAuth } from '../auth';

const CONFIDENCE_LABEL: Record<string, { text: string; tone: string }> = {
  high: { text: 'ตรงกับบัญชี LINE', tone: 'green' },
  medium: { text: 'เบอร์โทรตรงกัน', tone: 'amber' },
  low: { text: 'ชื่อคล้ายกัน', tone: '' },
};

export default function DonationDetail() {
  const { id = '' } = useParams();
  const { allowed } = useAuth();
  const [d, setD] = useState<Detail | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showVerify, setShowVerify] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setD(await api<Detail>(`/donations/${id}`));
    } catch (e) {
      setError(e);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api<{ data: CampaignSummary[] }>('/campaigns')
      .then((r) => setCampaigns(r.data))
      .catch(() => setCampaigns([]));
  }, []);

  async function act(fn: () => Promise<void>, message: string) {
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(message);
      await load();
    } catch (e) {
      setError(e);
    }
  }

  if (error && !d) return <ErrorAlert error={error} />;
  if (!d) return <Loading />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{formatSatang(d.amount)}</h1>
          <p>
            <span className="mono">{d.donationNo}</span> ·{' '}
            {DONATION_CHANNEL_LABEL_TH[d.channel] ?? d.channel} ·{' '}
            <span className={`badge ${d.status === 'verified' ? 'green' : d.status === 'pending_verify' ? 'amber' : ''}`}>
              {DONATION_STATUS_LABEL_TH[d.status] ?? d.status}
            </span>
          </p>
        </div>
        <Link className="btn secondary" to="/donations">
          ← กลับรายการ
        </Link>
      </div>

      <ErrorAlert error={error} />
      {notice && <div className="alert success">{notice}</div>}

      {d.status === 'pending_verify' && allowed('donation:verify') && (
        <div className="card">
          <h2>ตรวจสอบรายการนี้</h2>
          <p className="muted small" style={{ marginTop: 0 }}>
            เทียบยอดและวันเวลากับสลิปให้ตรงก่อนยืนยัน — ยอดที่ผู้บริจาคกรอกอาจไม่ตรงกับที่เข้าบัญชีจริง
          </p>
          <div className="btn-row">
            <button onClick={() => setShowVerify(true)}>ยืนยันการรับเงิน</button>
            <button
              className="secondary"
              onClick={() => {
                const reason = prompt('เหตุผลที่ปฏิเสธ');
                if (!reason) return;
                void act(
                  () =>
                    api(`/donations/${d.id}/reject`, {
                      method: 'POST',
                      body: { reason },
                    }),
                  'บันทึกการปฏิเสธแล้ว',
                );
              }}
            >
              ปฏิเสธ
            </button>
            <button
              className="secondary"
              onClick={() => {
                const reason = prompt('ระบุเลขที่รายการที่ซ้ำกัน');
                if (!reason) return;
                void act(
                  () =>
                    api(`/donations/${d.id}/reject`, {
                      method: 'POST',
                      body: { reason: `รายการซ้ำกับ ${reason}`, markDuplicate: true },
                    }),
                  'ทำเครื่องหมายว่าเป็นรายการซ้ำแล้ว',
                );
              }}
            >
              เป็นรายการซ้ำ
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-2">
        <div className="card">
          <h2>รายละเอียด</h2>
          <dl className="detail-list">
            <dt>จำนวนเงิน</dt>
            <dd style={{ fontWeight: 600 }}>{formatSatang(d.amount)}</dd>
            <dt>วันเวลาที่โอน</dt>
            <dd>{formatDateTime(d.donatedAt)}</dd>
            <dt>ชื่อบนสลิป</dt>
            <dd>{d.payerName ?? '—'}</dd>
            <dt>เลขอ้างอิง</dt>
            <dd className="mono">{d.slipRef ?? '—'}</dd>
            <dt>บัญชีปลายทาง</dt>
            <dd>{d.bankAccountLabel ?? '—'}</dd>
            <dt>แคมเปญ</dt>
            <dd>{d.campaignName ?? '—'}</dd>
            {d.earmarkNote && (
              <>
                <dt>ระบุวัตถุประสงค์</dt>
                <dd>{d.earmarkNote}</dd>
              </>
            )}
            <dt>บันทึกโดย</dt>
            <dd>
              {d.recordedVia === 'line_donor'
                ? 'ผู้บริจาคแจ้งเองผ่าน LINE'
                : (d.recordedByName ?? '—')}
            </dd>
            {d.verifiedAt && (
              <>
                <dt>ยืนยันโดย</dt>
                <dd>
                  {d.verifiedByName} · {formatDateTime(d.verifiedAt)}
                </dd>
              </>
            )}
            {d.rejectReason && (
              <>
                <dt>เหตุผลที่ปฏิเสธ</dt>
                <dd style={{ color: 'var(--c-danger)' }}>{d.rejectReason}</dd>
              </>
            )}
          </dl>
          {d.note && (
            <p className="small muted" style={{ marginBottom: 0 }}>
              {d.note}
            </p>
          )}
        </div>

        <div className="card">
          <h2>สลิป</h2>
          {d.slipUrl ? (
            <AuthedImage src={d.slipUrl} />
          ) : (
            <p className="muted small">ไม่มีสลิปแนบ</p>
          )}
          {d.slipSha256 && (
            <p className="muted small mono" style={{ wordBreak: 'break-all', marginBottom: 0 }}>
              sha256: {d.slipSha256.slice(0, 32)}…
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <h2>ผู้บริจาค</h2>
        {d.donorId ? (
          <p style={{ margin: 0 }}>
            <Link className="row-link" to={`/donors/${d.donorId}`}>
              {d.donorName}
            </Link>
          </p>
        ) : d.donorSuggestions.length > 0 ? (
          <>
            <p className="muted small" style={{ marginTop: 0 }}>
              ระบบเสนอผู้บริจาคที่น่าจะใช่ — <strong>ไม่ผูกอัตโนมัติ</strong> เพราะชื่อซ้ำในภาษาไทย
              เกิดบ่อย และการผูกผิดทำให้ยอดสะสมกับใบเสร็จออกผิดคน
            </p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th className="wrap">ชื่อ</th>
                    <th>เบอร์โทร</th>
                    <th>ยอดสะสม</th>
                    <th>เหตุผล</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {d.donorSuggestions.map((s) => {
                    const c = CONFIDENCE_LABEL[s.confidence] ?? { text: s.reason, tone: '' };
                    return (
                      <tr key={s.donorId}>
                        <td className="wrap">{s.displayName}</td>
                        <td>{s.phone ?? '—'}</td>
                        <td>{formatSatang(s.totalAmount)}</td>
                        <td>
                          <span className={`badge ${c.tone}`}>{c.text}</span>
                        </td>
                        <td>
                          {d.status === 'pending_verify' && allowed('donation:verify') && (
                            <button
                              className="link"
                              onClick={() =>
                                void act(
                                  () =>
                                    api(`/donations/${d.id}/verify`, {
                                      method: 'POST',
                                      body: { donorId: s.donorId },
                                    }),
                                  `ยืนยันและผูกกับ ${s.displayName} แล้ว`,
                                )
                              }
                            >
                              เลือกและยืนยัน
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="muted small" style={{ margin: 0 }}>
            ยังไม่ได้จับคู่ผู้บริจาค — ถ้าเป็นการบริจาคแบบไม่ประสงค์ออกนาม ยืนยันได้เลยโดยไม่ต้องระบุ
          </p>
        )}
      </div>

      {d.items.length > 0 && (
        <div className="card">
          <h2>สิ่งของที่บริจาค</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th className="wrap">รายการ</th>
                  <th>จำนวน</th>
                  <th>มูลค่าประเมิน</th>
                </tr>
              </thead>
              <tbody>
                {d.items.map((i) => (
                  <tr key={i.id}>
                    <td className="wrap">{i.itemName}</td>
                    <td>
                      {i.quantity.toLocaleString('th-TH')} {i.unit ?? ''}
                    </td>
                    <td>{i.estimatedValue ? formatSatang(i.estimatedValue) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <h2>ใบอนุโมทนาบัตร</h2>
        {d.receipt ? (
          <>
            <dl className="detail-list">
              <dt>เลขที่</dt>
              <dd className="mono">{d.receipt.receiptNo}</dd>
              <dt>ออกให้</dt>
              <dd>{d.receipt.receiptName}</dd>
              <dt>ออกเมื่อ</dt>
              <dd>{formatDateTime(d.receipt.issuedAt)}</dd>
            </dl>
            <div className="btn-row">
              <button
                className="secondary"
                onClick={() => void openAuthedDocument(d.receipt!.printUrl.replace(/^\/api/, ''))}
              >
                เปิดหน้าพิมพ์
              </button>
              <button
                className="secondary"
                onClick={() =>
                  void openAuthedDocument(
                    `${d.receipt!.printUrl.replace(/^\/api/, '')}?download=1`,
                    { download: `ใบอนุโมทนาบัตร-${d.receipt!.receiptNo}.html` },
                  )
                }
              >
                ดาวน์โหลด
              </button>
              {allowed('receipt:issue') && (
                <button
                  className="secondary"
                  onClick={() => {
                    const reason = prompt('เหตุผลที่ยกเลิกใบอนุโมทนาบัตร');
                    if (!reason) return;
                    void act(
                      () =>
                        api(`/receipts/${d.receipt!.id}/void`, {
                          method: 'POST',
                          body: { reason },
                        }),
                      'ยกเลิกใบอนุโมทนาบัตรแล้ว — เลขที่เดิมยังคงอยู่ในระบบเพื่อการตรวจสอบ',
                    );
                  }}
                >
                  ยกเลิกใบนี้
                </button>
              )}
            </div>
          </>
        ) : d.status === 'verified' && allowed('receipt:issue') ? (
          <button
            onClick={() =>
              void act(
                () => api(`/donations/${d.id}/receipt`, { method: 'POST', body: {} }),
                'ออกใบอนุโมทนาบัตรแล้ว',
              )
            }
          >
            ออกใบอนุโมทนาบัตร
          </button>
        ) : (
          <p className="muted small" style={{ margin: 0 }}>
            ออกใบอนุโมทนาบัตรได้หลังยืนยันการรับเงินแล้ว
          </p>
        )}
      </div>

      {showVerify && (
        <VerifyModal
          donation={d}
          campaigns={campaigns}
          onClose={() => setShowVerify(false)}
          onDone={(msg) => {
            setShowVerify(false);
            void act(async () => undefined, msg);
          }}
        />
      )}
    </>
  );
}

function VerifyModal({
  donation,
  campaigns,
  onClose,
  onDone,
}: {
  donation: Detail;
  campaigns: CampaignSummary[];
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [amountBaht, setAmountBaht] = useState(String(donation.amount / 100));
  const [slipRef, setSlipRef] = useState(donation.slipRef ?? '');
  const [campaignId, setCampaignId] = useState(donation.campaignId ?? '');
  const [issueReceipt, setIssueReceipt] = useState(false);
  const [createNew, setCreateNew] = useState(false);
  const [newDonorName, setNewDonorName] = useState(donation.payerName ?? '');
  const [newDonorPhone, setNewDonorPhone] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api(`/donations/${donation.id}/verify`, {
        method: 'POST',
        body: {
          amount: Math.round(Number(amountBaht) * 100),
          slipRef: slipRef.trim() || undefined,
          campaignId: campaignId || undefined,
          issueReceipt,
          createDonor: createNew
            ? {
                donorType: 'individual',
                displayName: newDonorName.trim(),
                phone: newDonorPhone.trim() || undefined,
                wantsReceipt: issueReceipt,
              }
            : undefined,
        },
      });
      onDone('ยืนยันการรับเงินเรียบร้อย — ระบบส่งข้อความขอบคุณให้ผู้บริจาคแล้ว');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="ยืนยันการรับเงิน" onClose={onClose}>
      <form onSubmit={submit}>
        <ErrorAlert error={error} />

        <div className="form-row">
          <div className="field">
            <label htmlFor="vamount">จำนวนเงินตามสลิป (บาท) *</label>
            <input
              id="vamount"
              type="text"
              inputMode="decimal"
              required
              value={amountBaht}
              onChange={(e) => setAmountBaht(e.target.value)}
            />
            <div className="hint">แก้ให้ตรงกับยอดที่เข้าบัญชีจริง</div>
          </div>
          <div className="field">
            <label htmlFor="vref">เลขอ้างอิงบนสลิป</label>
            <input id="vref" value={slipRef} onChange={(e) => setSlipRef(e.target.value)} />
            <div className="hint">กรอกไว้ช่วยกันบันทึกซ้ำในอนาคต</div>
          </div>
        </div>

        <div className="field">
          <label htmlFor="vcamp">แคมเปญ</label>
          <select id="vcamp" value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
            <option value="">ไม่เปลี่ยน</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {!donation.donorId && (
          <>
            <label className="checkbox" style={{ marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={createNew}
                onChange={(e) => setCreateNew(e.target.checked)}
              />
              <span>
                สร้างผู้บริจาครายใหม่
                <br />
                <span className="muted small">ไม่เลือก = บันทึกเป็นไม่ประสงค์ออกนาม</span>
              </span>
            </label>

            {createNew && (
              <div className="form-row">
                <div className="field">
                  <label htmlFor="ndname">ชื่อผู้บริจาค *</label>
                  <input
                    id="ndname"
                    required={createNew}
                    value={newDonorName}
                    onChange={(e) => setNewDonorName(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="ndphone">เบอร์โทร</label>
                  <input
                    id="ndphone"
                    value={newDonorPhone}
                    onChange={(e) => setNewDonorPhone(e.target.value)}
                    placeholder="0812345678"
                  />
                </div>
              </div>
            )}
          </>
        )}

        <label className="checkbox" style={{ marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={issueReceipt}
            onChange={(e) => setIssueReceipt(e.target.checked)}
          />
          <span>ออกใบอนุโมทนาบัตรทันที</span>
        </label>

        <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="secondary" onClick={onClose}>
            ยกเลิก
          </button>
          <button type="submit" disabled={busy}>
            {busy ? 'กำลังบันทึก…' : 'ยืนยันการรับเงิน'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** สลิปถูกป้องกันด้วย token — โหลดผ่าน fetch แล้วแปลงเป็น object URL */
function AuthedImage({ src }: { src: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const token = tokenStore.get();
        const res = await fetch(src, {
          headers: token ? { authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (failed) return <p className="muted small">โหลดรูปสลิปไม่สำเร็จ</p>;
  if (!url) {
    return <div style={{ height: 260, background: 'var(--c-bg)', borderRadius: 8 }} />;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img
        src={url}
        alt="สลิปการโอนเงิน"
        style={{ maxWidth: '100%', maxHeight: 420, borderRadius: 8, display: 'block' }}
      />
    </a>
  );
}
